import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Grid, Stack, Typography, Container } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";

import { LiveDrawCards } from "../../cards";
import ViewAllButton from "../viewAll";

import powerballImage from "../../../../../static/Images/powerball.png";
import revolverImage from "../../../../../static/Images/revolver.png";
import mineImage from "../../../../../static/Images/mine.png";

import { colors, LOTTERY_NAMES, LOTTERY_TYPE_IDS } from "../../../../../theme";
import { Path, getNetworkCurrency, DEFAULT_CURRENCY } from "../../../../../static/Constants";
import { safeFromWei, networkID } from "../../../../../static/utils";
import cinstance from "../../../../Service/randomcinstance";
import web3 from "../../../../Service/web3";

import "../liveGames/liveGames.css";

const withTimeout = (promise, ms = 4000) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("RPC Request Timeout")), ms)
    ),
  ]);
};

const isActiveLottery = (lottery) => {
  if (!lottery) return false;
  const statusId = Number(lottery.status ?? 0);
  const typeId = Number(lottery.lotteryType ?? 0);
  if (typeId === 0) {
    return statusId === 0 || statusId === 5;
  }
  if (typeId === 1) {
    return (statusId === 0 || statusId === 5) && Boolean(lottery.lotteryId);
  }
  if (typeId === 2) {
    return statusId === 0 || statusId === 3 || statusId === 5;
  }
  return statusId !== 2 && (statusId === 0 || statusId === 3 || statusId === 5);
};

const formatCountdown = (targetUnixSeconds, currentUnixSeconds) => {
  if (!targetUnixSeconds || Number(targetUnixSeconds) <= 0) return "Ready for Draw";
  const diff = Number(targetUnixSeconds) - currentUnixSeconds;
  if (diff <= 0) return "Drawing Soon";

  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  const seconds = diff % 60;

  const pad = (n) => String(n).padStart(2, "0");

  if (days > 0) {
    return `${days}d ${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
  }
  return `${pad(hours)} : ${pad(minutes)} : ${pad(seconds)}`;
};

const LiveGames = ({
  lotterylist1 = [],
  lotterylist2 = [],
  lotterylist3 = [],
  lotterylist4 = [],
  lotteries: propLotteries,
}) => {
  const theme = useTheme();
  const history = useHistory();

  const [fetchedLotteries, setFetchedLotteries] = useState([]);
  const [datesMap, setDatesMap] = useState({});
  const [currentNetId, setCurrentNetId] = useState(networkID);
  const [currentTimestamp, setCurrentTimestamp] = useState(() =>
    Math.floor(Date.now() / 1000)
  );

  // Live countdown timer interval ticking every 1 second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTimestamp(Math.floor(Date.now() / 1000));
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch on-chain lotteries and draw dates if needed
  useEffect(() => {
    let isMounted = true;

    const loadOnChainLotteriesAndDates = async () => {
      try {
        if (web3?.eth?.net?.getId) {
          try {
            const netId = await withTimeout(web3.eth.net.getId(), 2000);
            if (netId && isMounted) setCurrentNetId(netId);
          } catch (e) {}
        }

        if (!cinstance?.methods?.lotteryId) return;

        const totalIdsRes = await withTimeout(
          cinstance.methods.lotteryId().call(),
          3500
        ).catch(() => 1);

        const totalIds = Number(totalIdsRes) || 1;
        const fetchCalls = [];

        for (let i = 1; i < totalIds; i++) {
          const fetchMethod = cinstance.methods.lottery
            ? () => cinstance.methods.lottery(i).call()
            : () => cinstance.methods.lotteries(i).call();

          fetchCalls.push(withTimeout(fetchMethod(), 3500).catch(() => null));
        }

        const rawResults = (await Promise.all(fetchCalls)).filter(
          (item) => item && item.lotteryId && item.lotteryId !== "0"
        );

        const activeList = rawResults.filter(isActiveLottery);
        if (isMounted) {
          setFetchedLotteries(activeList);
        }

        // Fetch draw dates for active lotteries
        const dateCalls = activeList.map((item) =>
          withTimeout(cinstance.methods.lotteryDates(item.lotteryId).call(), 3500)
            .then((res) => ({ id: item.lotteryId, dates: res }))
            .catch(() => null)
        );

        const datesResults = (await Promise.all(dateCalls)).filter(Boolean);
        if (isMounted) {
          const newDatesMap = {};
          datesResults.forEach((d) => {
            if (d && d.id && d.dates) {
              newDatesMap[d.id] = d.dates;
            }
          });
          setDatesMap(newDatesMap);
        }
      } catch (err) {
        console.warn("Could not fetch on-chain data for LiveGames:", err);
      }
    };

    loadOnChainLotteriesAndDates();

    return () => {
      isMounted = false;
    };
  }, []);

  const currency = useMemo(
    () => getNetworkCurrency(currentNetId) || DEFAULT_CURRENCY,
    [currentNetId]
  );

  // Consolidate lotteries by type from props and on-chain fetch
  const { megaList, spinList, mineList, rocketList } = useMemo(() => {
    const all = [
      ...fetchedLotteries,
      ...(propLotteries || []),
      ...(lotterylist1 || []),
      ...(lotterylist2 || []),
      ...(lotterylist3 || []),
      ...(lotterylist4 || []),
    ];

    const uniqueMap = new Map();
    all.forEach((item) => {
      if (item && item.lotteryId && item.lotteryId !== "0") {
        uniqueMap.set(`${item.lotteryType}-${item.lotteryId}`, item);
      }
    });

    const deduped = Array.from(uniqueMap.values());

    const mega = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.MEGABET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    const spin = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.SPINBET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    const mine = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.MINEBET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    const rocket = deduped
      .filter((l) => Number(l.lotteryType) === LOTTERY_TYPE_IDS.ROCKETBET)
      .sort((a, b) => Number(b.lotteryId || 0) - Number(a.lotteryId || 0));

    return {
      megaList: mega,
      spinList: spin,
      mineList: mine,
      rocketList: rocket,
    };
  }, [fetchedLotteries, propLotteries, lotterylist1, lotterylist2, lotterylist3, lotterylist4]);

  // Compute live draws cards data dynamically
  const liveGamesList = useMemo(() => {
    // 1. MegaBET (Jackpot format with real draw countdown)
    const latestMega = megaList[0];
    const megaDateInfo = latestMega ? datesMap[latestMega.lotteryId] : null;
    const megaDrawTime = Number(
      megaDateInfo?.drawTime ||
        latestMega?.drawTime ||
        (latestMega?.deployedOn ? Number(latestMega.deployedOn) + 86400 : 0)
    );
    const megaCountdown = megaDrawTime > 0
      ? formatCountdown(megaDrawTime, currentTimestamp)
      : "12 : 00 : 00";

    const megaPickCount = Number(latestMega?.pickNumbers || 6);
    const megaNumbers = megaPickCount === 5
      ? [7, 14, 21, 28, 35]
      : [7, 11, 23, 32, 41, 17];

    const megaRoundNumber = latestMega?.lotteryId || "1";
    const megaRoute = latestMega?.lotteryId
      ? `/lottery/${latestMega.lotteryId}`
      : Path.allLotteries;

    // 2. SpinBET (Spinner / Wheel format)
    const latestSpin = spinList[0];
    const spinCapacity = Math.min(Math.max(Number(latestSpin?.capacity || 6), 6), 8);
    const spinNumbers = Array.from({ length: spinCapacity }, (_, i) => i + 1);
    const spinRoundNumber = latestSpin?.lotteryId || "1";
    const spinRoute = Path.Spinnerlottery;

    // 3. MineBET (Grid / Multiplier format)
    const latestMine = mineList[0];
    const mineRoundNumber = latestMine?.lotteryId || "1";
    const mineNumbers = ["2x", "5x", "10x", "25x", "50x"];
    const mineRoute = latestMine?.lotteryId
      ? `/minelottery/${latestMine.lotteryId}`
      : Path.allminelottery;

    return [
      {
        id: "megabet-draw",
        title: LOTTERY_NAMES.MEGABET,
        type: "JACKPOT",
        roundLabel: "Draw",
        roundNumber: megaRoundNumber,
        numbers: megaNumbers,
        countdown: megaCountdown,
        image: powerballImage,
        accentColor: colors.emerald[600],
        buttonText: "Play MegaBET",
        route: megaRoute,
      },
      {
        id: "spinbet-draw",
        title: LOTTERY_NAMES.SPINBET,
        type: "SPINNER",
        roundLabel: "Round",
        roundNumber: spinRoundNumber,
        numbers: spinNumbers,
        countdown: "Ready to Spin",
        image: revolverImage,
        accentColor: colors.primary.main,
        buttonText: "Spin Now",
        route: spinRoute,
      },
      {
        id: "minebet-draw",
        title: LOTTERY_NAMES.MINEBET,
        type: "MINES",
        roundLabel: "Round",
        roundNumber: mineRoundNumber,
        numbers: mineNumbers,
        countdown: "Instant Play",
        image: mineImage,
        accentColor: colors.purple[600],
        buttonText: "Play MineBET",
        route: mineRoute,
      },
    ];
  }, [megaList, spinList, mineList, datesMap, currentTimestamp]);
  return (
    <Box
      component="section"
      className="home-live-draws"
      sx={(theme) => ({
        width: "100%",
        pt: {
          xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
          sm: theme.custom.layoutSizes.section.paddingTop,
        },
        pb: {
          xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
          sm: theme.custom.layoutSizes.section.paddingBottom,
        },
        px: {
          ...theme.custom["home-section-left-padding"],
          xs: theme.custom.layoutSizes.mobileStandard.padding.leftRight,
        },
      })}
    >
      <Container
        maxWidth={false}
        disableGutters
        sx={{
          width: "100%",
          maxWidth: "1400px",
          mx: "auto",
          pt: 0,
          pb: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header Section */}
        <Stack
          className="live-draws-header"
          direction="row"
          alignItems={{ xs: "flex-start", sm: "flex-end" }}
          justifyContent="space-between"
          spacing={2}
          sx={{
            width: "100%",
            mb: (theme) => ({
              xs: theme.custom.layoutSizes.mobileStandard.spacing.textGroupBottomMargin,
              sm: theme.spacing(5),
            }),
          }}
        >
          <Box sx={(theme) => theme.typography.homeSection?.headerContainer || {}}>
           <Typography
             component="p"
             className="live-draws-eyebrow"
             sx={{
               marginBottom: {
                 xs: theme.custom.layoutSizes.mobileStandard.typography.eyebrow.marginBottom,
               },
               ...(theme.typography.homeSection?.eyebrow || {}),
               fontSize: {
                 xs: theme.custom.layoutSizes.mobileStandard.typography.eyebrow.fontSize,
               },
               color: "primary.main",
               "&::before": {
                 ...(theme.typography.homeSection?.eyebrow?.["&::before"] || {}),
                 background: (theme) => theme.palette.primary.main,
               },
             }}
           >
             LIVE DRAWS
           </Typography>

            <Typography
              component="h2"
              className="live-draws-title"
              sx={{
                fontSize: {
                  xs: theme.custom.layoutSizes.mobileStandard.typography.heading.fontSize,
                },
                ...(theme.typography.homeSection?.heading || {}),
                color: theme.custom.colors.home.navyDark,
              }}
            >
              Live & Transparent
            </Typography>
          </Box>

          <ViewAllButton
            label="View All Draws"
            to={Path.allLotteries}
          />
        </Stack>

        {/* Live Draw Cards Grid */}
        <Grid
          container
          spacing={3}
          className="live-draws-grid"
        >
          {liveGamesList.map((game) => (
            <Grid
              item
              xs={12}
              md={4}
              key={game.id || game.title}
              className="live-draw-card-wrapper"
            >
              <LiveDrawCards
                image={game.image}
                title={game.title}
                type={game.type}
                roundLabel={game.roundLabel}
                roundNumber={game.roundNumber}
                numbers={game.numbers}
                countdown={game.countdown}
                buttonText={game.buttonText}
                accentColor={game.accentColor}
                onClick={() => {
                  if (game.route) {
                    history.push(game.route);
                  }
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default LiveGames;